import { ActivityTimeline } from "../../components/ActivityTimeline";
import {
  BluetoothIcon,
  CheckIcon,
  InfoIcon,
  LockIcon,
  SafetyIcon,
  StopIcon
} from "../../components/Icons";
import { QIUI_LITTLE_DEVIL } from "../../lib/qiuiBridge";
import type { SessionState, User } from "../../types";

const bridgeDiagnosticLabels: Record<string, string> = {
  ble_connected: "Collar connected; awaiting protocol verification",
  ble_disconnected: "Collar Bluetooth link is disconnected",
  ble_characteristics_missing: "Required collar BLE characteristics were not found",
  ble_incompatible: "Connected device did not identify as a compatible Little Devil",
  ble_notify_subscription_failed: "Samsung could not subscribe to collar notifications",
  ble_write_unsupported: "The collar write characteristic does not advertise a supported write mode",
  ble_write_failed: "Samsung reported a Bluetooth write failure",
  protocol_check_running: "Non-output protocol check is running",
  protocol_no_notification: "No notification returned by the collar",
  protocol_write_rejected: "Android rejected the handshake write before sending it",
  protocol_write_failed: "The collar connection reported a failed handshake write",
  protocol_write_callback_timeout: "Android did not acknowledge the handshake write in time",
  protocol_write_accepted_no_notification:
    "Android completed the no-response write request, but the collar sent no notification",
  protocol_write_ack_no_notification:
    "Android completed the handshake write request, but the collar sent no notification",
  protocol_bad_length: "The collar returned an unexpected notification length",
  protocol_decrypt_failed: "The collar response did not match the expected encryption",
  protocol_token_invalid: "The decrypted collar response did not contain a valid token",
  manufacturer_compatibility_required:
    "Authorize QIUI compatibility on the wearer’s Samsung before checking the collar",
  manufacturer_transform_requested:
    "Requesting the registered-collar handshake directly from QIUI",
  manufacturer_transform_failed:
    "QIUI could not create the registered-collar diagnostic handshake",
  protocol_response_verifying:
    "The collar responded; the Samsung is verifying it directly with QIUI",
  manufacturer_response_verification_failed:
    "QIUI could not verify the collar response",
  protocol_verified: "Collar protocol handshake verified"
};
const notificationSubscriptionLabels: Record<string, string> = {
  unknown: "Not reported",
  requested: "Requested; awaiting Android callback",
  acknowledged: "Notification subscription completed by Android",
  failed: "Failed"
};
const writePropertyLabels: Record<string, string> = {
  unknown: "Not reported",
  with_response: "Write with response",
  without_response: "Write without response",
  both: "Both modes; using write without response",
  unsupported: "Unsupported"
};
const handshakeWriteLabels: Record<string, string> = {
  not_attempted: "Not attempted",
  queued: "Queued locally",
  api_accepted: "Accepted by Android; awaiting GATT callback",
  api_rejected: "Rejected by Android",
  gatt_acknowledged: "Write request completed by Android GATT",
  gatt_failed: "Failed in Android GATT",
  callback_timeout: "GATT callback timed out"
};

export function DevicesPage({
  user,
  state
}: {
  user: User;
  state: SessionState;
}) {
  const simulatorActive = state.device.mode === "simulator";
  const diagnosticConnected =
    state.device.mode === "diagnostic" && state.device.connected;
  const hardwareConnected =
    state.device.mode === "hardware" && state.device.connected;
  const deviceTitle = simulatorActive ? "Safety simulator" : "Little Devil";
  const deviceDetail = simulatorActive
    ? "No physical collar is connected"
    : hardwareConnected
      ? state.device.detail
    : diagnosticConnected
      ? "Real collar BLE diagnostics connected; output remains disabled"
      : "No physical collar is connected";
  const status = simulatorActive
    ? "SIMULATOR ACTIVE"
    : hardwareConnected
      ? state.device.outputReady
        ? "BACKGROUND BRIDGE READY"
        : "BACKGROUND BRIDGE LOCKED"
    : diagnosticConnected
      ? "BLE DIAGNOSTIC"
      : "DISCONNECTED";
  const bridgeDiagnostic = state.device.diagnosticCode
    ? bridgeDiagnosticLabels[state.device.diagnosticCode] ?? "Unknown bridge diagnostic"
    : "No detailed Samsung diagnostic received";
  const bridgeReportedAt = state.device.diagnosticReportedAt
    ? new Intl.DateTimeFormat([], {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit"
      }).format(state.device.diagnosticReportedAt)
    : "Not reported";
  const diagnosticWarning = [
    "ble_characteristics_missing",
    "ble_incompatible",
    "ble_notify_subscription_failed",
    "ble_write_unsupported",
    "ble_write_failed",
    "protocol_no_notification",
    "protocol_write_rejected",
    "protocol_write_failed",
    "protocol_write_callback_timeout",
    "protocol_write_accepted_no_notification",
    "protocol_write_ack_no_notification",
    "protocol_bad_length",
    "protocol_decrypt_failed",
    "protocol_token_invalid",
    "manufacturer_compatibility_required",
    "manufacturer_transform_failed",
    "manufacturer_response_verification_failed"
  ].includes(state.device.diagnosticCode ?? "");

  return (
    <main className="secondary-page">
      <header className="page-heading">
        <h1>Devices</h1>
        <p>The wearer’s Samsung app owns the persistent Bluetooth bridge.</p>
      </header>
      <section className="device-detail">
        <div className="device-detail__status">
          <BluetoothIcon />
          <span>
            <strong>{deviceTitle}</strong>
            <small>{deviceDetail}</small>
          </span>
          <span className={diagnosticConnected || hardwareConnected ? "connected-color" : ""}>
            {status}
          </span>
        </div>
        <div className="protocol-table">
          <span>Bridge owner</span>
          <strong>
            {hardwareConnected
              ? "Wearer’s Samsung app"
              : user.role === "wearer"
                ? "This browser"
                : "Wearer’s browser"}
          </strong>
          <span>Physical collar link</span>
          <strong>
            {hardwareConnected
              ? "Connected in the background"
              : diagnosticConnected
                ? "Connected for diagnostics only"
                : "Not connected"}
          </strong>
          <span>Samsung diagnostic</span>
          <strong className={diagnosticWarning ? "warning-color" : ""}>
            {bridgeDiagnostic}
          </strong>
          <span>Diagnostic received</span>
          <strong>{bridgeReportedAt}</strong>
          <span>Notifications observed</span>
          <strong>{state.device.notificationCount ?? 0}</strong>
          <span>Last notification length</span>
          <strong>
            {state.device.lastNotificationLength
              ? `${state.device.lastNotificationLength} bytes`
              : "None"}
          </strong>
          <span>Notification subscription</span>
          <strong>
            {notificationSubscriptionLabels[
              state.device.notificationSubscription ?? "unknown"
            ] ?? "Unknown"}
          </strong>
          <span>Write characteristic supports</span>
          <strong>
            {writePropertyLabels[state.device.writePropertyMode ?? "unknown"] ??
              "Unknown"}
          </strong>
          <span>Handshake write</span>
          <strong>
            {handshakeWriteLabels[state.device.handshakeWriteState ?? "not_attempted"] ??
              "Unknown"}
          </strong>
          <span>GATT write status</span>
          <strong>
            {state.device.gattWriteStatus == null
              ? "Not reported"
              : state.device.gattWriteStatus === 0
                ? "Success (0)"
                : `Failure (${state.device.gattWriteStatus})`}
          </strong>
          <span>Write acknowledgement time</span>
          <strong>
            {state.device.handshakeWriteElapsedMs == null
              ? "Not reported"
              : `${state.device.handshakeWriteElapsedMs} ms`}
          </strong>
          <span>First notification time</span>
          <strong>
            {state.device.firstNotificationElapsedMs == null
              ? "Not received"
              : `${state.device.firstNotificationElapsedMs} ms`}
          </strong>
          <span>Expected BLE service</span><code>{QIUI_LITTLE_DEVIL.service}</code>
          <span>Expected notification characteristic</span><code>{QIUI_LITTLE_DEVIL.notify}</code>
          <span>Expected write characteristic</span><code>{QIUI_LITTLE_DEVIL.write}</code>
          <span>Real output</span>
          <strong className={state.device.outputReady ? "connected-color" : "warning-color"}>
            {state.device.outputReady
              ? "Ready within wearer-approved limits"
              : "Locked pending off-body verification"}
          </strong>
        </div>
      </section>
      <section className="plain-section">
        <h2>Connect with the Samsung background bridge</h2>
        {user.role === "wearer" && (
          <a
            className="button button--primary"
            href="/api/android-bridge.apk"
            download="Velvet-Bridge.apk"
          >
            DOWNLOAD VELVET BRIDGE FOR SAMSUNG
          </a>
        )}
        <ol>
          <li>
            {user.role === "wearer"
              ? "Install Velvet Bridge on the Samsung phone and sign in once with the Wearer account."
              : "The wearer installs Velvet Bridge on the Samsung phone. The Controller account cannot pair Bluetooth."}
          </li>
          <li>
            Pair the collar inside Velvet Bridge. The persistent notification
            keeps the private relay and Bluetooth connection active in the background.
          </li>
          <li>
            In Velvet Bridge, authorize manufacturer compatibility with the QIUI
            account that owns this collar. The password goes directly from the
            Samsung to QIUI, the password field clears immediately, and the
            password is never stored.
          </li>
          <li>
            Keep the collar off every person. Hold its device button for three
            seconds until the green indicator flashes.
          </li>
          <li>
            Run the app’s non-output protocol check once. This diagnostic build
            cannot send a pulse; report the exact diagnostic result before proceeding.
          </li>
          <li>
            Do not arm or attempt output with this build. The persistent notification
            always provides <strong>STOP &amp; REVOKE</strong>.
          </li>
        </ol>
      </section>
      <section className="plain-section">
        <h2>Manufacturer compatibility</h2>
        <p>
          QIUI keeps the registered-collar command transformation on its server.
          Velvet Bridge contacts QIUI directly from the wearer’s Samsung to
          transform and verify collar frames. The QIUI password never passes
          through Velvet’s AWS relay and is not stored. This diagnostic build
          keeps every electrical-output path disabled while the exact collar
          handshake is verified off-body.
        </p>
      </section>
    </main>
  );
}

export function HistoryPage({ state }: { state: SessionState }) {
  return (
    <main className="secondary-page">
      <header className="page-heading">
        <h1>Shared history</h1>
        <p>Local event history records consent and stop paths, never message content.</p>
      </header>
      <ActivityTimeline events={state.events} />
    </main>
  );
}

export function SafetyPage() {
  return (
    <main className="secondary-page">
      <header className="page-heading">
        <h1>Safety</h1>
        <p>Control exists only while consent, presence, and the local bridge agree.</p>
      </header>
      <div className="safety-principles">
        <Principle Icon={StopIcon} title="Immediate stop">
          Release, pointer cancel, page blur, disconnect, heartbeat timeout, wearer
          revoke, session expiry, and server restart all stop or disarm.
        </Principle>
        <Principle Icon={LockIcon} title="Wearer-owned limits">
          The controller cannot raise intensity, extend hold duration, arm a
          session, pair Bluetooth, or change roles.
        </Principle>
        <Principle Icon={SafetyIcon} title="No automatic triggers">
          Motion, geofence, timed, and unattended stimulation modes are omitted.
        </Principle>
        <Principle Icon={InfoIcon} title="Manufacturer exclusions">
          Do not use with heart disease, pacemakers, severe hypertension,
          pregnancy, broken skin, metal jewelry near the device, or while impaired.
          Stop for redness, swelling, burning, numbness, or pain.
        </Principle>
      </div>
      <section className="plain-section safety-callout">
        <CheckIcon />
        <span>
          <h2>Physical reset comes first</h2>
          <p>
            Keep the manufacturer’s reset method and removal key within the
            wearer’s immediate reach. Video presence is not a physical rescue.
          </p>
        </span>
      </section>
    </main>
  );
}

function Principle({
  Icon,
  title,
  children
}: {
  Icon: typeof StopIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <Icon />
      <span>
        <h2>{title}</h2>
        <p>{children}</p>
      </span>
    </section>
  );
}
